/**
 * Modal bodies for the feedback-decision actions (archive+feedback / move).
 * Rendered inside the design-system Modal opened by ActionModals.tsx.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@finn-auto/design-system/atoms/input';
import {
  ModalHeader,
  ModalTitle } from '@finn-auto/design-system/atoms/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from '@finn-auto/design-system/atoms/select';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import * as api from '../../services/taActionBoardApi';
import type { FeedbackItem, RoleBoard, StageRef } from '../../types/taActionBoard';
import { COLORS } from './format';
import { buildInviteEmailBody, isOfferStage, moveTargetStages } from './modalDrafts';
import { EmailLibraryLink } from './EmailLibraryLink';
import { LanguageToggle } from './LanguageToggle';
import { OfferMoveBody } from './OfferMoveBody';
import { AttachmentPicker } from './AttachmentPicker';
import { LinkPicker } from './LinkPicker';
import type { OutboundEmail } from './outboundEmail';
import type { BryqKey } from '../../services/bryqApi';
import { BryqAlongsideField } from './BryqAlongsideField';
import { useBryqNote, withBryqNote } from './bryqNote';

export type { OutboundEmail };
import { ModalActions } from './ModalActions';
import { SendFallback } from './SendFallback';
import { SendOptIn } from './SendOptIn';

/**
 * ONE merged archive flow: the official Lever archive reason (AI suggestion
 * pre-selected) AND the candidate rejection email in the same modal.
 *
 * The email is drafted server-side and arrives with the board payload, so it renders
 * instantly. The recruiter reads exactly what the candidate will receive; confirming
 * counts as approval, and the same approved text is written to Lever as the feedback
 * note under the "freigegeben" prefix.
 *
 * Only the Tech-case templates place written feedback in the body — everywhere else the
 * wording is fixed, so there is nothing to edit and we do not pretend otherwise.
 */

/** What the recruiter approved in the modal — text only, never HTML. */
export interface ApprovedEmail {
  subject: string;
  /** Edited HTML from the contentEditable body; sanitised server-side before use. */
  bodyHtml: string;
  language: 'de' | 'en';
}


/**
 * Move modal — the automation's next-stage guess can be wrong, so the target
 * stage is a Select over Lever's interview-ish stages (defaulting to the guess),
 * plus a pre-drafted, editable interview-invite email that follows the chosen
 * stage and the DE/EN toggle. Confirming moves the Lever stage AND sends the
 * email from the recruiter's own Gmail, behind the board's 10-second undo. Copy
 * and mail-app appear only when the board cannot send (see SendFallback).
 */
export function MoveBody({
  role,
  item,
  stages,
  senderFirstName,
  boardEmail,
  gmailConnected,
  onConfirm,
  onCancel }: {
  role: RoleBoard;
  item: FeedbackItem;
  stages: StageRef[];
  senderFirstName: string;
  boardEmail: string;
  /** Whether a Google account is connected on the board — sending needs one. */
  gmailConnected: boolean;
  /** `email` non-null = also send it as the recruiter, behind the undo countdown. */
  onConfirm: (targetStage: StageRef | null, email: OutboundEmail | null) => void;
  onCancel: () => void;
}) {
  const candidateFirstName = item.name.trim().split(/\s+/)[0] || item.name;
  const options = moveTargetStages(stages, item);
  const [stageId, setStageId] = useState(item.nextStageId || options[0]?.id || '');
  /**
   * The recruiter's override when the target stage has several live links; null = the proxy's
   * derived default (their own link, else the newest), which is right in the common case.
   * Per send and per stage: a stage change drops it, because the other stage has other links.
   */
  const [linkChoice, setLinkChoice] = useState<string | null>(null);
  const chooseStage = (id: string) => {
    setStageId(id);
    setLinkChoice(null);
  };
  const target = options.find((s) => s.id === stageId) || null;
  const targetName = target?.name || item.nextStageName;
  // Moving to Offer is not an invitation — there is no interview to book and no
  // candidate mail to draft here. The offer itself is written in Lever, so this
  // path confirms the stage move and opens the profile (see OfferMoveBody).
  const toOffer = isOfferStage(target?.id ?? stageId, targetName);

  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const candidateEmail = item.email || '';

  // Draft the invite from the server, re-fetched whenever the target stage or
  // language changes: it mints/reuses the booking invite for the target-stage
  // link and fills in the interviewers. react-query (not a manual effect) keeps
  // this declarative and matches the rest of the app.
  const draftQuery = useQuery({
    queryKey: ['invite-draft', item.opportunityId, stageId, language, linkChoice],
    queryFn: () =>
      api.fetchInviteDraft({
        opportunityId: item.opportunityId,
        postingId: role.postingId,
        targetStageId: stageId,
        stageName: targetName,
        roleTitle: role.roleTitle,
        boardEmail,
        senderName: senderFirstName,
        language,
        // The booking URL in the body is minted on THIS link, and the body is what gets sent —
        // so the picker, the preview and the send can only ever name one link.
        linkId: linkChoice }),
    enabled: !!stageId && !toOffer,
    retry: false,
    staleTime: 60_000 });
  const loading = draftQuery.isFetching;

  // Edits are overrides on top of the fetched draft (null = follow the draft),
  // so a stage/language change re-drafts cleanly and edits reset there — no
  // state-syncing effect needed.
  const [subjectEdit, setSubjectEdit] = useState<string | null>(null);
  const [bodyEdit, setBodyEdit] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<OutboundEmail['attachment']>(null);
  const [bryq, setBryq] = useState<BryqKey | null>(null);
  const canSend = gmailConnected && !!candidateEmail;
  /**
   * Armed by DEFAULT when sending is possible.
   *
   * It used to default off, which made copy-and-paste the discoverable path and
   * left the board's own log without the email the candidate actually got. The
   * check that matters is still there and unchanged — the recruiter reads the
   * text, the confirm button says "Move & send email", and a 10-second countdown
   * runs before anything leaves — so this is a default, not an automation
   * (Theresa, Sep 2026).
   */
  const [sendEnabled, setSendEnabled] = useState(canSend);
  const edited = subjectEdit !== null || bodyEdit !== null;

  const fallbackSubject =
    language === 'de'
      ? `Nächste Schritte bei FINN — ${role.roleTitle}`
      : `Next steps at FINN — ${role.roleTitle}`;
  const draft = draftQuery.data;
  const subject = subjectEdit ?? draft?.subject ?? fallbackSubject;
  // On a fetch error the draft is undefined — fall back to the built-in body so
  // the modal is never empty.
  const bryqNote = useBryqNote(language);
  const draftText =
    draft?.body ??
    (draftQuery.isError ? buildInviteEmailBody(candidateFirstName, targetName, senderFirstName) : '');
  // "Send Bryq in parallel": the paragraph follows the tick, in the draft and in an edited text.
  const body = bodyEdit ?? (draftText ? withBryqNote(draftText, bryqNote, bryq !== null) : '');
  const onBryq = (next: BryqKey | null) => {
    setBryq(next);
    if (bodyEdit !== null) setBodyEdit(withBryqNote(bodyEdit, bryqNote, next !== null));
  };
  const meta = draft
    ? {
        source: draft.source,
        bookingUrl: draft.bookingUrl,
        linkFound: draft.meta.linkFound }
    : null;

  // Changing stage/language re-drafts; discard edits first (with a heads-up).
  const guardRedraft = (change: () => void) => {
    if (edited && !window.confirm('This will re-draft the email and discard your edits. Continue?')) {
      return;
    }
    setSubjectEdit(null);
    setBodyEdit(null);
    change();
  };

  // OFFER: no interview to invite to — a plain confirmation, then Lever.
  if (toOffer) {
    return (
      <OfferMoveBody
        item={item}
        options={options}
        stageId={stageId}
        onStageId={chooseStage}
        targetName={targetName}
        onConfirm={() => onConfirm(target, null)}
        onCancel={onCancel}
      />
    );
  }

  return (
    <>
      <ModalHeader>
        <ModalTitle>
          Move {item.name} to {targetName}
        </ModalTitle>
      </ModalHeader>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Check the target stage (the automation only guesses it) and the invite email below.
          Confirming moves {candidateFirstName} to “{targetName}” in Lever
          {canSend ? ' and sends the email from your Gmail.' : '.'}
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[14rem] flex-1">
            <Select value={stageId} onValueChange={(v) => guardRedraft(() => chooseStage(v))}>
              {/* w-full: the DS trigger defaults to w-max, which cut long names off */}
              <SelectTrigger label="Move to stage" className="w-full">
                <SelectValue placeholder="Choose a stage" />
              </SelectTrigger>
              <SelectContent>
                {options.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.id === item.nextStageId ? ' (suggested)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* The shared control, so all four draft modals behave identically. */}
          <LanguageToggle
            value={language}
            onChange={(l) => guardRedraft(() => setLanguage(l))}
          />
        </div>
        {/* Renders nothing unless this stage has more than one live link. */}
        <LinkPicker
          options={draft?.meta.linkOptions}
          value={draft?.meta.linkId}
          ownEmail={boardEmail}
          disabled={loading}
          onChange={(id) => guardRedraft(() => setLinkChoice(id))}
        />

        {/* Where this draft came from + whether the booking link resolved. */}
        <div className="flex flex-wrap gap-2 text-xs">
          {meta?.linkFound ? (
            <span className="rounded px-1.5 py-0.5" style={{ backgroundColor: '#DCF5E4', color: '#136C34' }}>
              {/* Three sources, three names — 'library' used to mislabel as "Default template". */}
              {meta.source === 'link'
                ? 'Custom wording for this link'
                : meta.source === 'library'
                  ? 'Email Library template'
                  : 'Default template'}{' '}
              · booking link {meta.bookingUrl ? 'added' : 'missing'}
            </span>
          ) : (
            !loading && (
              <span className="rounded px-1.5 py-0.5" style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}>
                {draft?.meta.roleClosed
                  ? 'This role is closed in Lever, so its interview links take no new bookings — no booking link inserted.'
                  : 'No interview link for this role+stage — no booking link inserted. Create one on the Interview Links page.'}
              </span>
            )
          )}
        </div>

        <Input
          label="Subject"
          value={subject}
          disabled={loading}
          onChange={(e) => setSubjectEdit((e.target as HTMLInputElement).value)}
        />
        <Textarea
          label={loading ? 'Email draft — drafting…' : 'Email draft'}
          // DS Textarea maps only rows 2–5 to a height and silently falls back to the
          // 3-row height above that, collapsing the box under its own label.
          rows={5}
          textAreaClassName="min-h-[16rem]"
          value={loading ? '⏳ Drafting the invite (booking link + interviewers)…' : body}
          disabled={loading}
          onChange={(e) => setBodyEdit((e.target as HTMLTextAreaElement).value)}
        />
        {/* Only for 'library' drafts — renders nothing without a key ('link' wording lives on the
            Interview Links page, which the chips above already point at). */}
        <div className="mt-1 text-right">
          <EmailLibraryLink templateKey={draft?.templateKey} />
        </div>
        {/* Case material for the roles that share it WITH the invitation. Held in
            memory only — it rides along on this one email and is not stored. */}
        <AttachmentPicker attachment={attachment} onChange={setAttachment} disabled={loading} />
        {sendEnabled && (
          <BryqAlongsideField
            value={bryq}
            onChange={onBryq}
            language={language}
            opportunityId={item.opportunityId}
            disabled={loading}
          />
        )}
        <SendOptIn
          canSend={canSend}
          gmailConnected={gmailConnected}
          candidateEmail={candidateEmail}
          checked={sendEnabled}
          disabled={loading}
          onChange={setSendEnabled}
        />
        {/* Only when the board genuinely cannot send — see SendFallback. */}
        {!canSend && (
          <SendFallback
            subject={subject}
            body={body}
            candidateEmail={candidateEmail || null}
            gmailConnected={gmailConnected}
            hasAttachment={!!attachment}
          />
        )}
      </div>
      <ModalActions
        confirmLabel={sendEnabled ? 'Move & send email' : 'Confirm move in Lever'}
        confirmDisabled={!stageId || loading}
        onConfirm={() => onConfirm(target, sendEnabled ? { subject, body, attachment, bryq } : null)}
        onCancel={onCancel}
      />
    </>
  );
}

